// hooks/useLusukuRiders.js
// ─────────────────────────────────────────────────────────────────────────────
// The platform rider roster, over the shared socket.
//
// Eight order-details screens each opened their own socket and wired the same
// four handlers (connect -> emit getRiders, initialRidersList, lusukuRiders,
// updatedRidersList) with their own copies of the loading flag. They drifted:
// some never cleared `loadingRiders` when the socket failed, so the picker sat
// on "Searching for riders..." forever and the Assign button stayed dead with
// nothing on screen explaining why.
//
// One hook, one behaviour: riders, a loading flag that always resolves, and an
// `error` the caller can actually show.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { io } from "socket.io-client";
import socketAuthOptions from "../utilities/socketAuth";
import { getUserFromLocalStorage } from "../utilities/user";

const CONNECT_TIMEOUT_MS = 12000;

const useLusukuRiders = () => {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const user = getUserFromLocalStorage();
    const socket = io(
      process.env.REACT_APP_SERVER_URL,
      socketAuthOptions({ userId: user?.result?._id })
    );

    let settled = false;
    const settle = (list, failure) => {
      settled = true;
      if (list) setRiders(list);
      setError(failure || null);
      setLoading(false);
    };

    /* Without this the picker can sit on "Searching for riders..." forever when
       the socket never answers, which reads as a broken button rather than a
       connection the operator can retry. */
    const timer = setTimeout(() => {
      if (!settled) {
        setLoading(false);
        setError("Could not reach the rider service. Reload to try again.");
      }
    }, CONNECT_TIMEOUT_MS);

    socket.on("connect", () => socket.emit("getRiders"));
    socket.on("initialRidersList", (list) => settle(list || []));
    socket.on("lusukuRiders", (list) => settle(list || []));
    socket.on("updatedRidersList", (list) => setRiders(list || []));
    socket.on("connect_error", () =>
      settle(null, "Could not reach the rider service. Reload to try again.")
    );

    return () => {
      clearTimeout(timer);
      socket.disconnect();
    };
  }, []);

  return { riders, loading, error };
};

export default useLusukuRiders;
